import type { ServiceRequest } from '@medplum/fhirtypes';

/**
 * Frontend mirror of the care-plan review markers stamped on patient to-do
 * ServiceRequests — see `apps/api-nest/src/care-plans/pending-change.ts`
 * (the backend source of truth for these semantics, exercised by
 * `care-plan-review.decide.spec.ts`). The systems are environment-independent
 * constants rooted at the centralized Eluve FHIR namespace (ENG-4575), so
 * mirroring the strings is safe; the backend code is not importable from
 * frontend code. Same seam as the observations/conditions cards'
 * review-markers mirrors — consolidate all three together when the dedupe
 * pass comes.
 */
const ELUVE_FHIR_NAMESPACE = 'https://eluve.com';

/** meta.tag system carrying the human-review state of a plan item. */
export const REVIEW_STATUS_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/review-status`;
export const REVIEW_STATUS_PENDING = 'pending';
/** Human confirmed the item. */
export const REVIEW_STATUS_ATTESTED = 'attested';
/** Human rejected the item — hidden from every list, status untouched. */
export const REVIEW_STATUS_REJECTED = 'rejected';

/** The review-status tag's code, or undefined when untagged. */
export function readReviewStatus(
  serviceRequest: ServiceRequest,
): string | undefined {
  return serviceRequest.meta?.tag?.find(
    (tag) => tag.system === REVIEW_STATUS_SYSTEM,
  )?.code;
}

/** Still awaiting human review — the unverified badge and the decide guard. */
export function isPendingReview(serviceRequest: ServiceRequest): boolean {
  return readReviewStatus(serviceRequest) === REVIEW_STATUS_PENDING;
}

/**
 * Rejected by a reviewer. The decide transition only swaps the tag — the
 * ServiceRequest stays `active` — so status-filtered reads must drop these
 * client-side.
 */
export function isRejected(serviceRequest: ServiceRequest): boolean {
  return readReviewStatus(serviceRequest) === REVIEW_STATUS_REJECTED;
}

/**
 * Replace the `review-status` meta.tag (pending → attested / rejected),
 * preserving every other tag — same transition the backend reviewer applies.
 */
export function setReviewStatusTag(
  serviceRequest: ServiceRequest,
  code: string,
): ServiceRequest {
  const others = (serviceRequest.meta?.tag ?? []).filter(
    (tag) => tag.system !== REVIEW_STATUS_SYSTEM,
  );
  return {
    ...serviceRequest,
    meta: {
      ...serviceRequest.meta,
      tag: [...others, { system: REVIEW_STATUS_SYSTEM, code }],
    },
  };
}
